/**
 * CapacityShowFilter.tsx
 * The "Show" segment-visibility toggle for the split-availability capacity grid
 * (redesign 2026-07-14, SPEC.md §2): show/hide the Committed / Uncommitted / Free
 * segments in every cell's bar. Rendered as clean colored checkboxes — the
 * checkbox colour IS the segment legend (no pills, nothing to clip). Purely a
 * controlled UI-state toggle — no persistence. Colors via theme tokens — no hex
 * literals (CODING-STANDARDS).
 */

import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SplitVisibility } from './capacitySplitModel';

export interface CapacityShowFilterProps {
  value: SplitVisibility;
  onChange: (next: SplitVisibility) => void;
}

export const CapacityShowFilter: React.FC<CapacityShowFilterProps> = ({ value, onChange }) => {
  const theme = useTheme();

  const items: Array<{ key: keyof SplitVisibility; label: string; color: string }> = [
    { key: 'committed', label: 'Committed', color: theme.schedule.committed },
    { key: 'uncommitted', label: 'Uncommitted', color: theme.schedule.capacityUncommitted },
    { key: 'free', label: 'Free', color: theme.palette.text.disabled },
  ];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
      <Typography variant="caption" sx={{ mr: 0.5, fontSize: '0.62rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Show
      </Typography>
      {items.map((it) => {
        const active = value[it.key];
        return (
          <FormControlLabel
            key={it.key}
            sx={{ m: 0, mr: 0.5 }}
            control={
              <Checkbox
                size="small"
                disableRipple
                checked={active}
                onChange={() => onChange({ ...value, [it.key]: !active })}
                sx={{ p: 0.35, color: it.color, '&.Mui-checked': { color: it.color } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: active ? 'text.primary' : 'text.disabled' }}>
                {it.label}
              </Typography>
            }
          />
        );
      })}
    </Box>
  );
};
